import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { PaymentMethodSelector } from "@/components/PaymentMethodSelector";
import { Card } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { PAYMENT_METHODS } from "@/utils/paymentMethods";
import type { PaymentProvider } from "@/types/payment";

interface PaymentDetails {
  enabled_methods: PaymentProvider[];
  [key: string]: any;
}

export function PaymentMethodsManager({ onClose }: { onClose?: () => void }) {
  const [selectedMethod, setSelectedMethod] = useState<string>("");
  const { toast } = useToast();
  const queryClient = useQueryClient();

  // Fetch host's payment settings
  const { data: profile, isLoading } = useQuery({
    queryKey: ['host-profile'],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Not authenticated");

      const { data, error } = await supabase
        .from('profiles')
        .select('payment_details')
        .eq('id', user.id)
        .maybeSingle();

      if (error) throw error;
      return data;
    },
  });

  const mutation = useMutation({
    mutationFn: async (enabledMethods: PaymentProvider[]) => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Not authenticated");

      const paymentDetails: PaymentDetails = {
        ...profile?.payment_details as PaymentDetails || {},
        enabled_methods: enabledMethods
      };

      const { error } = await supabase
        .from('profiles')
        .update({
          payment_details: paymentDetails
        })
        .eq('id', user.id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['host-profile'] });
      toast({
        title: "Success",
        description: "Payment methods updated successfully",
      });
    },
    onError: (error) => {
      console.error("Error updating payment methods:", error);
      toast({
        title: "Error",
        description: "Failed to update payment methods",
        variant: "destructive",
      });
    },
  });

  const handleToggleMethod = (methodId: PaymentProvider, enabled: boolean) => {
    const currentMethods = (profile?.payment_details as PaymentDetails)?.enabled_methods || [];
    const newMethods = enabled
      ? [...currentMethods, methodId]
      : currentMethods.filter(m => m !== methodId);
    
    mutation.mutate(newMethods);
  };

  if (isLoading) {
    return <div>Loading...</div>;
  }

  const enabledMethods = (profile?.payment_details as PaymentDetails)?.enabled_methods || [];

  return (
    <Card className="p-6">
      <h3 className="text-lg font-semibold mb-4">Manage Payment Methods</h3>
      
      <div className="space-y-6">
        <div className="space-y-4">
          <h4 className="text-sm font-medium">Card Payments</h4>
          {PAYMENT_METHODS.filter(m => m.id === 'credit-card').map((method) => (
            <div key={method.id} className="flex items-center justify-between p-4 border rounded-lg">
              <div>
                <p className="font-medium">{method.label}</p>
                <p className="text-sm text-muted-foreground">Processing time: {method.processingTime}</p>
              </div>
              <Switch
                checked={enabledMethods.includes(method.id)}
                onCheckedChange={(checked) => handleToggleMethod(method.id, checked)}
              />
            </div>
          ))}
        </div>

        <div className="space-y-4">
          <h4 className="text-sm font-medium">Mobile Money</h4>
          {PAYMENT_METHODS.filter(m => ['mtn', 'airtel', 'vodafone', 'hubtel'].includes(m.id)).map((method) => (
            <div key={method.id} className="flex items-center justify-between p-4 border rounded-lg">
              <div>
                <p className="font-medium">{method.label}</p>
                <p className="text-sm text-muted-foreground">Processing time: {method.processingTime}</p>
              </div>
              <Switch
                checked={enabledMethods.includes(method.id)}
                onCheckedChange={(checked) => handleToggleMethod(method.id, checked)}
              />
            </div>
          ))}
        </div>

        <div className="space-y-4">
          <h4 className="text-sm font-medium">Other Payment Methods</h4>
          {PAYMENT_METHODS.filter(m => ['paystack', 'paypal', 'crypto'].includes(m.id)).map((method) => (
            <div key={method.id} className="flex items-center justify-between p-4 border rounded-lg">
              <div>
                <p className="font-medium">{method.label}</p>
                <p className="text-sm text-muted-foreground">Processing time: {method.processingTime}</p>
              </div>
              <Switch
                checked={enabledMethods.includes(method.id)}
                onCheckedChange={(checked) => handleToggleMethod(method.id, checked)}
              />
            </div>
          ))}
        </div>
      </div>

      {onClose && (
        <div className="flex justify-end space-x-2 mt-6">
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={onClose}>
            Done
          </Button>
        </div>
      )}
    </Card>
  );
}